import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Group as GroupIcon,
  DoneAll as DoneAllIcon,
  Receipt as ReceiptIcon,
  MonetizationOn as MonetizationOnIcon,
  Speed as SpeedIcon,
  Phone as PhoneIcon,
  EmojiEvents,
  AttachMoney as HighValueIcon,
  Warning as RiskIcon
} from '@mui/icons-material';
import { useLeadReport } from '../../../contexts/LeadReportContext';

const SummaryKPIs = ({ dateRangeState, selectedTeams = [] }) => {
  const theme = useTheme();
  const { fetchGeneralReport } = useLeadReport();
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKPIs = async () => {
      if (!dateRangeState) return;
      
      setLoading(true);
      const { currentStart, currentEnd } = dateRangeState;
      const startDate = currentStart.format('YYYY-MM-DD');
      const endDate = currentEnd.format('YYYY-MM-DD');

      try {
        // Fetch all KPI data in parallel
        const [
          totalLeads,
          leadContactRate,
          closedDeals,
          avgResponseTime,
          leadQualified,
          siteVisitRate,
          topPerformer,
          highValueLeads,
          leadsAtRisk
        ] = await Promise.all([
          fetchGeneralReport('new_leads', startDate, endDate, selectedTeams),
          fetchGeneralReport('lead_contact_rate', startDate, endDate, selectedTeams),
          fetchGeneralReport('closed_deals', startDate, endDate, selectedTeams),
          fetchGeneralReport('avg_response_time', startDate, endDate, selectedTeams),
          fetchGeneralReport('lead_qualified', startDate, endDate, selectedTeams),
          fetchGeneralReport('site_visit_rate', startDate, endDate, selectedTeams),
          fetchGeneralReport('top_performing_agents', startDate, endDate, selectedTeams),
          fetchGeneralReport('high_value_leads', startDate, endDate, selectedTeams),
          fetchGeneralReport('leads_at_risk', startDate, endDate, selectedTeams)
        ]);

        const kpis = [
          {
            title: 'New Leads',
            value: totalLeads?.value?.toString() || '0',
            change: totalLeads?.change?.value || '0%',
            isPositive: totalLeads?.change?.isPositive !== false,
            icon: <GroupIcon />,
            color: 'primary',
            subtitle: 'This Period',
            description: 'Count of new leads created in period',
            trend: totalLeads?.change?.trend || 'up'
          },
          {
            title: 'Qualified Leads',
            value: leadQualified?.value?.toString() || '0',
            change: leadQualified?.change?.value || '0%',
            isPositive: leadQualified?.change?.isPositive !== false,
            icon: <DoneAllIcon />,
            color: 'success',
            subtitle: totalLeads?.value ? `${Math.round((leadQualified?.value || 0) / totalLeads.value * 100)}% of Total` : '0% of Total',
            description: 'Leads passed qualification stage',
            trend: leadQualified?.change?.trend || 'up'
          },
          {
            title: 'Deals Closed',
            value: closedDeals?.value?.toString() || '0',
            change: closedDeals?.change?.value || '0%',
            isPositive: closedDeals?.change?.isPositive !== false,
            icon: <ReceiptIcon />,
            color: 'info',
            subtitle: totalLeads?.value ? `${Math.round((closedDeals?.value || 0) / totalLeads.value * 100)}% Conversion` : '0% Conversion',
            description: 'Closed deals',
            trend: closedDeals?.change?.trend || 'down'
          },
          {
            title: 'Lead Contact Rate',
            value: leadContactRate?.value !== undefined && leadContactRate?.value !== null
              ? `${Math.min(100, Math.max(0, Math.round(leadContactRate.value)))}%`
              : '0%',
            change: leadContactRate?.change?.value || '0%',
            isPositive: leadContactRate?.change?.isPositive !== false,
            icon: <PhoneIcon />,
            color: 'success',
            subtitle: 'Success Rate',
            description: '% of leads that received at least one successful connect',
            trend: leadContactRate?.change?.trend || 'up'
          },
          {
            title: 'Avg Response Time',
            value: avgResponseTime?.value || 'N/A',
            change: avgResponseTime?.change?.value || '0%',
            isPositive: avgResponseTime?.change?.isPositive !== false,
            icon: <SpeedIcon />,
            color: 'secondary',
            subtitle: 'Time to First Contact',
            description: 'Average time from lead creation to first contact',
            trend: avgResponseTime?.change?.trend || 'up'
          },
          {
            title: 'Site Visit Rate',
            value: siteVisitRate?.value?.toString() || '0%',
            change: siteVisitRate?.change?.value || '0%',
            isPositive: siteVisitRate?.change?.isPositive !== false,
            icon: <ReceiptIcon />,
            color: 'warning',
            subtitle: siteVisitRate?.absolute_value !== undefined 
              ? `${siteVisitRate.absolute_value.toLocaleString()} Site Visits`
              : 'Visit Conversion',
            description: 'Percentage of leads that resulted in site visits',
            trend: siteVisitRate?.change?.trend || 'up'
          },
          {
            title: 'Top Performing Agent',
            value: topPerformer?.name || 'N/A',
            change: topPerformer?.rate || '0%',
            isPositive: true,
            icon: <EmojiEvents />,
            color: 'primary',
            subtitle: 'Conversion Rate',
            description: 'Agent with max performance score',
            trend: topPerformer?.change?.trend || 'up'
          },
          {
            title: 'High Value Leads',
            value: highValueLeads?.value?.toString() || highValueLeads?.value || '0',
            change: highValueLeads?.change?.value || '0%',
            isPositive: highValueLeads?.change?.isPositive !== false,
            icon: <HighValueIcon />,
            color: 'success',
            subtitle: 'Budget > 1 Cr',
            description: 'Leads with budget more than 1 Cr',
            trend: highValueLeads?.change?.trend || 'up'
          },
          {
            title: 'High Intent Leads at Risk',
            value: leadsAtRisk?.value?.toString() || '0',
            change: leadsAtRisk?.change?.value || '0%',
            isPositive: leadsAtRisk?.change?.isPositive !== false,
            icon: <RiskIcon />,
            color: 'error',
            subtitle: 'No Activity (7 Days)',
            description: 'Leads with no follow-up activity in last 7 days',
            trend: leadsAtRisk?.change?.trend || 'up'
          }
        ];

        setKpiData(kpis);
      } catch (error) {
        console.error('Error fetching KPIs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchKPIs();
  }, [dateRangeState, selectedTeams, fetchGeneralReport]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Grid container spacing={3}>
          {kpiData.map((kpi, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3,
                  height: '100%',
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette[kpi.color].main, 0.2)}`,
                  background: 'background.paper',
                  boxShadow: `0 2px 8px ${alpha(theme.palette[kpi.color].main, 0.08)}`,
                  transition: 'all 0.3s ease',
                }}
              >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    {kpi.title}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette[kpi.color].main,
                      mb: 0.5,
                      fontSize: '1.75rem',
                    }}
                  >
                    {kpi.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                    }}
                  >
                    {kpi.subtitle}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette[kpi.color].main, 0.1),
                    color: theme.palette[kpi.color].main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {kpi.icon}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Chip
                  icon={kpi.isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  label={kpi.change}
                  size="small"
                  sx={{
                    bgcolor: alpha(
                      kpi.isPositive ? theme.palette.success.main : theme.palette.error.main,
                      0.1
                    ),
                    color: kpi.isPositive ? theme.palette.success.main : theme.palette.error.main,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  }}
                >
                  vs Previous Period
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>

    </>
  );
};

export default SummaryKPIs;
